import api from "../api/axios";
import { isNativeAndroid, isNativeApp, isNativeIOS } from "./runtime";
import {
  buildNativeStoreValidatorSuccess,
  getValidatorStoreProductId,
} from "./nativeStorePayload";
import { hydrateNativeStoreProducts } from "./nativeStorePricing";
import { createNativeStoreDeliveryCoordinator } from "./nativeStoreLifecycle";
import {
  getPendingRestorableTransactionIds,
  NATIVE_STORE_FINISHED_STATE,
} from "./nativeStoreRestore";
import { buildGooglePlaySubscriptionUpgradeData } from "./nativeSubscriptionChange";

let initializationPromise = null;
let initializedPlatform = "";
let initializedProductIds = [];
let latestDelivery = null;
const deliveryListeners = new Set();
const pendingDeliveriesByProduct = new Map();
const failureListeners = new Set();
const deliveryCoordinator = createNativeStoreDeliveryCoordinator();
let restorePromise = null;
let catalogRefreshPromise = null;

const notifyDelivery = (delivery) => {
  latestDelivery = delivery || null;
  if (!latestDelivery) return;
  const productId = String(latestDelivery?.store_product_id || "").trim();
  if (productId) {
    pendingDeliveriesByProduct.set(productId, latestDelivery);
  }
  deliveryListeners.forEach((listener) => {
    try {
      listener(latestDelivery);
    } catch (error) {
      console.error("Native store delivery listener failed", error);
    }
  });
};

const notifyFailure = (failure) => {
  if (!failure) return;
  failureListeners.forEach((listener) => {
    try {
      listener(failure);
    } catch (error) {
      console.error("Native store failure listener failed", error);
    }
  });
};

const takePendingDelivery = (productId) => {
  const normalizedProductId = String(productId || "").trim();
  if (!normalizedProductId) return null;
  const delivery = pendingDeliveriesByProduct.get(normalizedProductId) || null;
  if (delivery) pendingDeliveriesByProduct.delete(normalizedProductId);
  return delivery;
};

const sameProductId = (left, right) => (
  String(left || "").trim() === String(right || "").trim()
);

const nativeStoreError = (error, fallbackMessage) => {
  const normalized = new Error(error?.message || fallbackMessage);
  normalized.code = error?.code;
  normalized.platform = error?.platform;
  normalized.productId = error?.productId;
  return normalized;
};

export const subscribeNativeStoreDelivery = (listener) => {
  if (typeof listener !== "function") return () => {};
  deliveryListeners.add(listener);
  return () => deliveryListeners.delete(listener);
};

export const subscribeNativeStoreFailure = (listener) => {
  if (typeof listener !== "function") return () => {};
  failureListeners.add(listener);
  return () => failureListeners.delete(listener);
};

const nativePlatform = () => {
  if (isNativeIOS()) return "ios";
  if (isNativeAndroid()) return "android";
  return "web";
};

const storePlatformName = () => {
  if (isNativeIOS()) return "ios-appstore";
  if (isNativeAndroid()) return "android-playstore";
  return "";
};

const mapProductType = (ProductType, productType) => {
  if (productType === "subscription") return ProductType.PAID_SUBSCRIPTION;
  if (productType === "non_consumable") return ProductType.NON_CONSUMABLE;
  return ProductType.CONSUMABLE;
};

export async function initializeNativeStore({ onDelivered } = {}) {
  if (!isNativeApp()) return { products: [] };

  if (typeof onDelivered === "function") {
    deliveryListeners.add(onDelivered);
  }

  const platform = nativePlatform();
  if (initializationPromise && initializedPlatform === platform) {
    return initializationPromise;
  }

  initializationPromise = (async () => {
    const [{ store, ProductType, Platform, ErrorCode }, catalogResponse] = await Promise.all([
      import("capacitor-plugin-cdv-purchase"),
      api.get("/miniapp/native-store/catalog/"),
    ]);

    const storePlatform = isNativeIOS() ? Platform.APPLE_APPSTORE : Platform.GOOGLE_PLAY;
    const products = Array.isArray(catalogResponse?.data?.products)
      ? catalogResponse.data.products
      : [];
    const applicationUsername = String(
      catalogResponse?.data?.application_username || ""
    ).trim();
    if (applicationUsername) {
      store.applicationUsername = applicationUsername;
      store.obfuscator = "disabled";
    }
    const registeredProducts = products
      .filter((product) => product?.store_product_id && product?.product_type)
      .map((product) => ({
        id: product.store_product_id,
        type: mapProductType(ProductType, product.product_type),
        platform: storePlatform,
      }));

    initializedPlatform = platform;
    initializedProductIds = registeredProducts.map((product) => product.id);

    if (!registeredProducts.length) {
      return { products: [] };
    }

    store.validator_privacy_policy = ["fraud", "support"];
    store.validator = async (body, callback) => {
      const transaction = body?.transaction || {};
      const productId = getValidatorStoreProductId(body);
      try {
        const response = await api.post("/miniapp/native-store/complete/", {
          platform,
          store_product_id: productId,
          transaction,
        });
        const delivery = response?.data?.delivery || null;
        if (!deliveryCoordinator.stage(delivery)) {
          throw new Error("Verified native purchase has no transaction id.");
        }
        callback(buildNativeStoreValidatorSuccess({
          productId,
          storePlatform,
          transactionType: storePlatformName(),
          responseData: response?.data,
          transaction,
        }));
      } catch (error) {
        const failure = {
          platform,
          store_product_id: productId,
          status: error?.response?.status || 500,
          message:
            error?.response?.data?.error ||
            error?.response?.data?.detail ||
            error?.message ||
            "Native purchase verification failed.",
        };
        notifyFailure(failure);
        callback({
          ok: false,
          status: failure.status,
          code: ErrorCode.VERIFICATION_FAILED,
          message: failure.message,
          data: { latest_receipt: true },
        });
      }
    };

    store.register(registeredProducts);
    store.when()
      .approved((transaction) => transaction.verify())
      .verified((receipt) => {
        void Promise.resolve(receipt.finish()).catch((error) => {
          console.error("Failed to finish verified native purchase", error);
        });
      })
      .finished((transaction) => {
        const delivery = deliveryCoordinator.takeForFinishedTransaction(transaction);
        if (delivery) notifyDelivery(delivery);
      });

    await store.initialize([storePlatform]);
    const hydratedProducts = hydrateNativeStoreProducts(
      products,
      (productId) => store.get(productId, storePlatform)
    );
    return { products: hydratedProducts };
  })();

  return initializationPromise;
}

export async function refreshNativeStoreProducts() {
  if (!isNativeApp()) return { products: [] };
  if (catalogRefreshPromise) return catalogRefreshPromise;

  catalogRefreshPromise = (async () => {
    const initialized = await initializeNativeStore();
    const { store, Platform } = await import("capacitor-plugin-cdv-purchase");
    const storePlatform = isNativeIOS() ? Platform.APPLE_APPSTORE : Platform.GOOGLE_PLAY;
    const previousUpdateInterval = store.minTimeBetweenUpdates;
    try {
      // The App Store account can change while the app stays open. The plugin's
      // default ten-minute throttle would otherwise keep the previous price.
      store.minTimeBetweenUpdates = 0;
      await store.update();
    } finally {
      store.minTimeBetweenUpdates = previousUpdateInterval;
    }
    return {
      products: hydrateNativeStoreProducts(
        initialized.products,
        (productId) => store.get(productId, storePlatform)
      ),
    };
  })();

  try {
    return await catalogRefreshPromise;
  } finally {
    catalogRefreshPromise = null;
  }
}

export async function purchaseNativeStoreProduct(
  productId,
  { replaceSubscriptionProductId = "" } = {}
) {
  if (!isNativeApp()) {
    throw new Error("Native store purchases are available only in the mobile app.");
  }
  let unsubscribeDelivery = () => {};
  let unsubscribeFailure = () => {};
  let timeoutId;
  let deliverySettled = false;
  let resolveDelivery = () => {};
  let rejectDelivery = () => {};
  const deliveryPromise = new Promise((resolve, reject) => {
    resolveDelivery = resolve;
    rejectDelivery = reject;
    unsubscribeDelivery = subscribeNativeStoreDelivery((delivery) => {
      if (!sameProductId(delivery?.store_product_id, productId)) return;
      deliverySettled = true;
      pendingDeliveriesByProduct.delete(String(productId || "").trim());
      window.clearTimeout(timeoutId);
      unsubscribeDelivery();
      unsubscribeFailure();
      resolve(delivery);
    });
    unsubscribeFailure = subscribeNativeStoreFailure((failure) => {
      if (!sameProductId(failure?.store_product_id, productId)) return;
      deliverySettled = true;
      window.clearTimeout(timeoutId);
      unsubscribeDelivery();
      unsubscribeFailure();
      reject(new Error(failure?.message || "Native purchase verification failed."));
    });
  });
  // The validator can fail while store initialization is still resolving.
  // Attach a handler immediately and let the awaited promise below propagate
  // the same error to the purchase caller.
  void deliveryPromise.catch(() => {});

  try {
    // Subscribe before initialization so an unfinished StoreKit transaction
    // restored during startup cannot be delivered before this call starts
    // listening for it.
    const [{ store, Platform }, initResult] = await Promise.all([
      import("capacitor-plugin-cdv-purchase"),
      initializeNativeStore(),
    ]);
    const storePlatform = isNativeIOS()
      ? Platform.APPLE_APPSTORE
      : Platform.GOOGLE_PLAY;
    let product = store.get(productId, storePlatform);
    if (!product) {
      throw new Error("Native store product is not available.");
    }

    if (deliverySettled) {
      const delivery = await deliveryPromise;
      return { product, catalog: initResult?.products || [], delivery };
    }

    const restoredDelivery = takePendingDelivery(productId);
    if (restoredDelivery) {
      deliverySettled = true;
      unsubscribeDelivery();
      unsubscribeFailure();
      return {
        product,
        catalog: initResult?.products || [],
        delivery: restoredDelivery,
      };
    }

    let offer = typeof product.getOffer === "function" ? product.getOffer() : product.offers?.[0];
    if (!offer) {
      // Product metadata can become stale while the app stays in the
      // background. Refresh once before declaring the Play/App Store item
      // unavailable; never retry an order after the billing sheet opened.
      await store.update();
      product = store.get(productId, storePlatform);
      offer = typeof product?.getOffer === "function" ? product.getOffer() : product?.offers?.[0];
    }
    if (!offer) {
      throw new Error("Native store offer is not available.");
    }
    timeoutId = window.setTimeout(() => {
      const lateDelivery = takePendingDelivery(productId);
      if (lateDelivery) {
        deliverySettled = true;
        unsubscribeDelivery();
        unsubscribeFailure();
        resolveDelivery(lateDelivery);
        return;
      }
      deliverySettled = true;
      unsubscribeDelivery();
      unsubscribeFailure();
      const timeoutError = new Error("Native purchase was not verified in time.");
      timeoutError.code = "native_store_delivery_timeout";
      rejectDelivery(timeoutError);
    }, 45000);
    const orderAdditionalData = isNativeAndroid() && replaceSubscriptionProductId
      ? buildGooglePlaySubscriptionUpgradeData(
          store.localReceipts,
          replaceSubscriptionProductId
        )
      : undefined;
    // Staff telemetry must never block or change the store purchase itself.
    try {
      void api.post("/miniapp/native-store/checkout-started/", {
        platform: nativePlatform(),
        store_product_id: productId,
        attempt_id: crypto.randomUUID(),
      }).catch(() => {});
    } catch {
      // Telemetry is optional, including on older WebViews.
    }
    const error = await offer.order(orderAdditionalData);
    if (error) {
      throw nativeStoreError(error, "Native store purchase failed.");
    }
    const delivery = await deliveryPromise;
    return { product, catalog: initResult?.products || [], delivery };
  } catch (error) {
    window.clearTimeout(timeoutId);
    unsubscribeDelivery();
    unsubscribeFailure();
    throw error;
  }
}

const waitForRestoredTransactions = async ({
  store,
  restorableProductIds,
  finishedState,
  failureRef,
  timeoutMs = 45000,
  minimumWaitMs = 1500,
}) => {
  const startedAt = Date.now();
  // Native restore completion is emitted after restored transactions, but the
  // JS validator/finalizer chain is asynchronous. Give it one short turn to
  // materialize transactions before polling their final states.
  await new Promise((resolve) => window.setTimeout(resolve, 500));
  while (Date.now() - startedAt < timeoutMs) {
    if (failureRef.current) throw failureRef.current;
    const pendingIds = getPendingRestorableTransactionIds(
      store.localTransactions,
      restorableProductIds,
      finishedState
    );
    if (!pendingIds.length && Date.now() - startedAt >= minimumWaitMs) return;
    await new Promise((resolve) => window.setTimeout(resolve, 150));
  }
  const timeoutError = new Error("Restored purchases were not verified in time.");
  timeoutError.code = "native_store_restore_timeout";
  throw timeoutError;
};

export async function restoreNativeStorePurchases() {
  if (!isNativeApp()) {
    throw new Error("Purchase restoration is available only in the mobile app.");
  }
  if (restorePromise) return restorePromise;

  restorePromise = (async () => {
    const [{ store }, initResult] = await Promise.all([
      import("capacitor-plugin-cdv-purchase"),
      initializeNativeStore(),
    ]);
    const catalog = Array.isArray(initResult?.products) ? initResult.products : [];
    const restorableProductIds = catalog
      .filter(
        (product) =>
          product?.product_type === "non_consumable" ||
          product?.product_type === "subscription"
      )
      .map((product) => String(product?.store_product_id || "").trim())
      .filter(Boolean);
    const restorableProductIdSet = new Set(restorableProductIds);
    const deliveries = [];
    const failureRef = { current: null };
    const unsubscribeDelivery = subscribeNativeStoreDelivery((delivery) => {
      const productId = String(delivery?.store_product_id || "").trim();
      if (!restorableProductIdSet.has(productId)) return;
      takePendingDelivery(productId);
      deliveries.push(delivery);
    });
    const unsubscribeFailure = subscribeNativeStoreFailure((failure) => {
      const productId = String(failure?.store_product_id || "").trim();
      if (!restorableProductIdSet.has(productId)) return;
      failureRef.current = new Error(
        failure?.message || "Restored purchase verification failed."
      );
    });

    try {
      const restoreError = await store.restorePurchases();
      if (restoreError) {
        throw new Error(restoreError.message || "Purchase restoration failed.");
      }
      await waitForRestoredTransactions({
        store,
        restorableProductIds,
        // capacitor-plugin-cdv-purchase exposes TransactionState only through
        // CdvPurchase, not as a named ESM export. The runtime value is the
        // stable transaction state string used by store.localTransactions.
        finishedState: NATIVE_STORE_FINISHED_STATE,
        failureRef,
      });
      const response = await api.post("/miniapp/native-store/restore/");
      return {
        deliveries,
        restored: response?.data?.restored || {},
      };
    } finally {
      unsubscribeDelivery();
      unsubscribeFailure();
    }
  })();

  try {
    return await restorePromise;
  } finally {
    restorePromise = null;
  }
}

export async function manageNativeStoreSubscriptions() {
  if (!isNativeApp()) {
    throw new Error("Subscription management is available only in the mobile app.");
  }
  const [{ store, Platform }, initResult] = await Promise.all([
    import("capacitor-plugin-cdv-purchase"),
    initializeNativeStore(),
  ]);
  const error = await store.manageSubscriptions(
    isNativeIOS() ? Platform.APPLE_APPSTORE : Platform.GOOGLE_PLAY
  );
  if (error) {
    throw new Error(error.message || "Subscription management could not be opened.");
  }
  return initResult;
}

export async function findNativeStoreProduct({ kind, skinId, boostDurationDays } = {}) {
  const { products } = await initializeNativeStore();
  return (products || []).find((product) => {
    if (kind && product.kind !== kind) return false;
    if (skinId != null && Number(product.skin_id) !== Number(skinId)) return false;
    if (
      boostDurationDays != null &&
      Number(product.boost_duration_days) !== Number(boostDurationDays)
    ) {
      return false;
    }
    return product.store_available;
  });
}

export async function findNativeSubscriptionProduct(tier, plan) {
  const { products } = await initializeNativeStore();
  return (products || []).find(
    (product) =>
      product.kind === "subscription" &&
      product.subscription_tier === tier &&
      product.subscription_plan === plan &&
      product.store_available
  );
}

export function getInitializedNativeProductIds() {
  return initializedProductIds.slice();
}
